import { useState, useEffect, useCallback, Fragment } from 'react'
import { supabase } from '../../api/supabase'
import { usePagination } from '../../hooks/usePagination'
import PaginationControls from '../../components/PaginationControls'
import { getCachedCount, setCachedCount } from '../../lib/countCache'

interface AuditLogRow {
  id: string
  table_name: string
  record_id: string | null
  operation: 'INSERT' | 'UPDATE' | 'DELETE'
  user_id: string | null
  user_email: string | null
  old_data: Record<string, unknown> | null
  new_data: Record<string, unknown> | null
  changed_fields: string[] | null
  created_at: string
}

const WATCHED_TABLES = [
  'loading_lists', 'devanning_plans', 'devanning_plan_items',
  'devannings', 'devanning_items', 'devanning_outputs', 'delivery_notes',
  'customers', 'jobs', 'parcel_issues', 'commercial_invoices', 'hs_code_rules',
  'company_settings',
]

const OP_BADGE: Record<string, string> = {
  INSERT: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  UPDATE: 'bg-blue-100  text-blue-800  dark:bg-blue-900/30  dark:text-blue-300',
  DELETE: 'bg-red-100   text-red-800   dark:bg-red-900/30   dark:text-red-300',
}

const OP_ROW_BG: Record<string, string> = {
  INSERT: 'bg-green-50/60 dark:bg-green-900/10',
  UPDATE: 'bg-blue-50/40  dark:bg-blue-900/10',
  DELETE: 'bg-red-50/60   dark:bg-red-900/10',
}

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

function fmtTs(ts: string) {
  return new Date(ts).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

// ─── Deep diff ────────────────────────────────────────────────────────────────

interface DiffEntry {
  path: string
  oldVal: unknown
  newVal: unknown
  type: 'changed' | 'added' | 'removed'
}

function deepDiff(a: unknown, b: unknown, path = ''): DiffEntry[] {
  if (a === b) return []
  if (a === null || a === undefined || b === null || b === undefined) {
    return [{ path: path || '(root)', oldVal: a, newVal: b, type: 'changed' }]
  }
  if (typeof a !== typeof b) {
    return [{ path: path || '(root)', oldVal: a, newVal: b, type: 'changed' }]
  }
  if (typeof a !== 'object') {
    return [{ path: path || '(root)', oldVal: a, newVal: b, type: 'changed' }]
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return [{ path: path || '(root)', oldVal: a, newVal: b, type: 'changed' }]
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    const results: DiffEntry[] = []
    const maxLen = Math.max(a.length, b.length)
    for (let i = 0; i < maxLen; i++) {
      const p = path ? `${path}[${i}]` : `[${i}]`
      if (i >= a.length)      results.push({ path: p, oldVal: undefined, newVal: b[i], type: 'added' })
      else if (i >= b.length) results.push({ path: p, oldVal: a[i], newVal: undefined, type: 'removed' })
      else                    results.push(...deepDiff(a[i], b[i], p))
    }
    return results
  }
  const ao = a as Record<string, unknown>
  const bo = b as Record<string, unknown>
  const allKeys = new Set([...Object.keys(ao), ...Object.keys(bo)])
  const results: DiffEntry[] = []
  for (const key of allKeys) {
    const p = path ? `${path}.${key}` : key
    if (!(key in ao)) results.push({ path: p, oldVal: undefined, newVal: bo[key], type: 'added' })
    else if (!(key in bo)) results.push({ path: p, oldVal: ao[key], newVal: undefined, type: 'removed' })
    else results.push(...deepDiff(ao[key], bo[key], p))
  }
  return results
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtVal(v: unknown): string {
  if (v === undefined) return '(none)'
  if (v === null) return 'null'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

function isJsonbLike(v: unknown): boolean {
  return v !== null && typeof v === 'object'
}

const MAX_CHARS = 200

function TruncVal({ text, className }: { text: string; className?: string }) {
  const [full, setFull] = useState(false)
  const long = text.length > MAX_CHARS
  return (
    <span className={className}>
      {long && !full ? text.slice(0, MAX_CHARS) + '…' : text}
      {long && (
        <button onClick={e => { e.stopPropagation(); setFull(f => !f) }}
          className="ml-1 text-blue-500 hover:underline text-xs font-normal not-italic">
          {full ? 'show less' : 'show full'}
        </button>
      )}
    </span>
  )
}

function CopyBtn({ value }: { value: string }) {
  const [ok, setOk] = useState(false)
  return (
    <button
      title="Copy value"
      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(value).then(() => { setOk(true); setTimeout(() => setOk(false), 1500) }) }}
      className="ml-1 shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
    >
      {ok ? <span className="text-green-500 text-xs">✓</span>
           : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>}
    </button>
  )
}

// ─── JSONB field card (deep diff) ─────────────────────────────────────────────

function JsonbCard({ name, oldVal, newVal }: { name: string; oldVal: unknown; newVal: unknown }) {
  const [open, setOpen] = useState(false)
  const diffs = deepDiff(oldVal, newVal)
  const label = diffs.length === 1 ? '1 change' : `${diffs.length} changes`

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/60 hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
      >
        <span className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-200">{name}</span>
        <span className="flex items-center gap-2">
          <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{label}</span>
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {diffs.map((d, i) => {
            const oldStr = d.type !== 'added'   ? fmtVal(d.oldVal) : ''
            const newStr = d.type !== 'removed' ? fmtVal(d.newVal) : ''
            return (
              <div key={i} className={`flex items-start gap-3 px-3 py-1.5 text-xs font-mono ${
                d.type === 'added'   ? 'bg-green-50 dark:bg-green-900/15' :
                d.type === 'removed' ? 'bg-red-50   dark:bg-red-900/15'   :
                                       'bg-white     dark:bg-gray-800'
              }`}>
                <span className="text-gray-400 dark:text-gray-500 shrink-0 min-w-0 break-all">{d.path}</span>
                {d.type === 'added' ? (
                  <span className="text-green-700 dark:text-green-400 flex items-center gap-1">
                    <span className="text-green-400 font-bold">+</span>
                    <TruncVal text={newStr} />
                    <CopyBtn value={newStr} />
                  </span>
                ) : d.type === 'removed' ? (
                  <span className="text-red-700 dark:text-red-400 flex items-center gap-1">
                    <span className="text-red-400 font-bold">−</span>
                    <TruncVal text={oldStr} />
                    <CopyBtn value={oldStr} />
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 flex-wrap">
                    <TruncVal text={oldStr} className="text-red-600 dark:text-red-400 line-through" />
                    <CopyBtn value={oldStr} />
                    <span className="text-gray-400">→</span>
                    <TruncVal text={newStr} className="text-green-700 dark:text-green-400" />
                    <CopyBtn value={newStr} />
                  </span>
                )}
              </div>
            )
          })}
          {diffs.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400 italic">No leaf-level changes detected.</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Scalar field row ─────────────────────────────────────────────────────────

function ScalarRow({ name, oldVal, newVal }: { name: string; oldVal: unknown; newVal: unknown }) {
  const oldStr = fmtVal(oldVal)
  const newStr = fmtVal(newVal)
  return (
    <tr className="border-b border-gray-100 dark:border-gray-800 last:border-0">
      <td className="px-3 py-1.5 font-mono text-xs font-medium text-gray-600 dark:text-gray-400 w-40 align-top">{name}</td>
      <td className="px-3 py-1.5 align-top">
        <span className="flex items-start gap-1">
          <TruncVal text={oldStr} className="font-mono text-xs text-red-700 dark:text-red-400 line-through break-all" />
          <CopyBtn value={oldStr} />
        </span>
      </td>
      <td className="px-3 py-1.5 align-top">
        <span className="flex items-start gap-1">
          <TruncVal text={newStr} className="font-mono text-xs text-green-700 dark:text-green-400 break-all" />
          <CopyBtn value={newStr} />
        </span>
      </td>
    </tr>
  )
}

// ─── INSERT / DELETE record preview ───────────────────────────────────────────

function RecordPreview({ data, color }: { data: Record<string, unknown>; color: string }) {
  const [showAll, setShowAll] = useState(false)
  const simple  = Object.entries(data).filter(([, v]) => !isJsonbLike(v))
  const complex = Object.entries(data).filter(([, v]) => isJsonbLike(v))
  const shown   = showAll ? simple : simple.slice(0, 8)
  return (
    <div>
      <table className="text-xs w-full">
        <tbody>
          {shown.map(([k, v]) => {
            const s = fmtVal(v)
            return (
              <tr key={k} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                <td className="px-2 py-1 font-mono text-gray-500 w-40 align-top">{k}</td>
                <td className={`px-2 py-1 font-mono ${color} break-all align-top`}>
                  <span className="flex items-start gap-1">
                    <TruncVal text={s} /><CopyBtn value={s} />
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {simple.length > 8 && !showAll && (
        <button onClick={() => setShowAll(true)} className="text-xs text-blue-500 hover:underline mt-1 px-2">
          +{simple.length - 8} more scalar fields…
        </button>
      )}
      {complex.length > 0 && (
        <div className="mt-2 px-2">
          <span className="text-xs text-gray-400">Nested: {complex.map(([k]) => k).join(', ')}</span>
        </div>
      )}
    </div>
  )
}

// ─── Main DiffView ────────────────────────────────────────────────────────────

function DiffView({ log }: { log: AuditLogRow }) {
  if (log.operation === 'DELETE') {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
        <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">Deleted record</p>
        {log.old_data && <RecordPreview data={log.old_data} color="text-red-700 dark:text-red-400" />}
      </div>
    )
  }
  if (log.operation === 'INSERT') {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
        <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-2">New record</p>
        {log.new_data && <RecordPreview data={log.new_data} color="text-green-700 dark:text-green-400" />}
      </div>
    )
  }

  // UPDATE
  const fields = log.changed_fields ?? []
  if (fields.length === 0) {
    return <p className="text-xs text-gray-400 italic">No field changes recorded.</p>
  }

  const scalarFields = fields.filter(f => {
    const ov = log.old_data?.[f]
    const nv = log.new_data?.[f]
    return !isJsonbLike(ov) && !isJsonbLike(nv)
  })
  const jsonbFields = fields.filter(f => {
    const ov = log.old_data?.[f]
    const nv = log.new_data?.[f]
    return isJsonbLike(ov) || isJsonbLike(nv)
  })

  return (
    <div>
      {/* Summary bar */}
      <div className="flex items-center gap-2 mb-3 px-2 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <span className="text-xs font-semibold text-blue-700 dark:text-blue-300">
          {fields.length} field{fields.length !== 1 ? 's' : ''} changed:
        </span>
        <span className="text-xs font-mono text-blue-600 dark:text-blue-400">{fields.join(', ')}</span>
      </div>

      {/* Scalar fields table */}
      {scalarFields.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-2">
          <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/60 grid grid-cols-3 text-xs font-semibold text-gray-500">
            <span>Field</span>
            <span className="text-red-600 dark:text-red-400">Before</span>
            <span className="text-green-600 dark:text-green-400">After</span>
          </div>
          <table className="w-full">
            <tbody>
              {scalarFields.map(f => (
                <ScalarRow key={f} name={f} oldVal={log.old_data?.[f]} newVal={log.new_data?.[f]} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* JSONB field cards */}
      {jsonbFields.map(f => (
        <JsonbCard key={f} name={f} oldVal={log.old_data?.[f]} newVal={log.new_data?.[f]} />
      ))}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AuditLog() {
  const [logs, setLogs]       = useState<AuditLogRow[]>([])
  const [total, setTotal]     = useState<number | null>(null)
  const { page, setPage, pageSize, setPageSize, range } = usePagination('audit-log', 100)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const [filterTable, setFilterTable]   = useState('')
  const [filterEmail, setFilterEmail]   = useState('')
  const [filterFrom, setFilterFrom]     = useState('')
  const [filterTo, setFilterTo]         = useState('')
  const [filterRecord, setFilterRecord] = useState('')
  // Bumped by applyFilters/clearFilters so the fetch effect below re-runs even when page is
  // already 0 (setPage(0) no-ops in that case) -- calling fetchLogs() directly from those
  // handlers would use a stale closure, since setFilterX() hasn't re-rendered yet at that point.
  const [filtersVersion, setFiltersVersion] = useState(0)

  // Z35.1: migrated off this page's own bottom-only, fixed-50, no-URL pagination onto the
  // shared usePagination/PaginationControls pattern (also used by Jobs/Customers/Ports/
  // Carriers) -- 30s-cached count, top+bottom controls, 25/50/100/200/500/All, ?page=&size=.
  const fetchLogs = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const countCacheKey = `audit-log:${JSON.stringify({ filterTable, filterEmail, filterFrom, filterTo, filterRecord })}`
      const cachedCount = getCachedCount(countCacheKey)

      let q = supabase
        .from('audit_logs')
        .select('*', cachedCount === undefined ? { count: 'exact' } : undefined)
        .order('created_at', { ascending: false })

      if (filterTable)  q = q.eq('table_name', filterTable)
      if (filterEmail)  q = q.ilike('user_email', `%${filterEmail}%`)
      if (filterFrom)   q = q.gte('created_at', filterFrom)
      if (filterTo)     q = q.lte('created_at', filterTo + 'T23:59:59Z')
      if (filterRecord) q = q.ilike('record_id', `%${filterRecord}%`)

      if (range) q = q.range(range[0], range[1])

      const { data, error: err, count } = await q
      if (err) throw err
      setLogs((data as AuditLogRow[]) || [])
      if (cachedCount !== undefined) {
        setTotal(cachedCount)
      } else {
        setTotal(count ?? 0)
        setCachedCount(countCacheKey, count ?? 0)
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [filterTable, filterEmail, filterFrom, filterTo, filterRecord, range])

  useEffect(() => { fetchLogs() }, [page, pageSize, filtersVersion]) // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => { setPage(0); setFiltersVersion(v => v + 1); setExpanded(new Set()) }
  const clearFilters = () => {
    setFilterTable(''); setFilterEmail(''); setFilterFrom(''); setFilterTo(''); setFilterRecord('')
    setPage(0); setFiltersVersion(v => v + 1); setExpanded(new Set())
  }

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const hasFilters = !!(filterTable || filterEmail || filterFrom || filterTo || filterRecord)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Log</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {loading || total === null ? 'Loading…' : `${total.toLocaleString()} record${total !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Table</label>
            <select className={inputCls} value={filterTable} onChange={e => setFilterTable(e.target.value)}>
              <option value="">All tables</option>
              {WATCHED_TABLES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">User email</label>
            <input className={inputCls} placeholder="Search email…" value={filterEmail}
              onChange={e => setFilterEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From date</label>
            <input type="date" className={inputCls} value={filterFrom}
              onChange={e => setFilterFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To date</label>
            <input type="date" className={inputCls} value={filterTo}
              onChange={e => setFilterTo(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Record ID</label>
            <input className={inputCls} placeholder="UUID…" value={filterRecord}
              onChange={e => setFilterRecord(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && applyFilters()} />
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={applyFilters}
            className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Search
          </button>
          {hasFilters && (
            <button onClick={clearFilters}
              className="px-4 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-sm rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {/* Z35.1: controls at both top and bottom of the list */}
      {!error && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mb-4">
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-20 text-center text-gray-400">Loading…</div>
      ) : logs.length === 0 ? (
        <div className="py-20 text-center text-gray-400">No records found.</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Table</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Op</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Record ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Changed fields</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {logs.map(log => (
                  <Fragment key={log.id}>
                    <tr className={`transition-colors hover:brightness-95 ${OP_ROW_BG[log.operation] ?? ''}`}>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500 whitespace-nowrap">
                        {fmtTs(log.created_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 dark:text-gray-300 max-w-[180px]">
                        <span className="truncate block" title={log.user_email ?? undefined}>
                          {log.user_email ?? <em className="text-gray-400">system</em>}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                          {log.table_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${OP_BADGE[log.operation] ?? ''}`}>
                          {log.operation}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400 max-w-[140px]">
                        <span className="truncate block" title={log.record_id ?? undefined}>
                          {log.record_id ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 max-w-[220px]">
                        {log.operation === 'UPDATE' && log.changed_fields?.length
                          ? (
                            <span className="font-mono" title={log.changed_fields.join(', ')}>
                              {log.changed_fields.slice(0, 4).join(', ')}
                              {log.changed_fields.length > 4 && (
                                <span className="text-gray-400"> +{log.changed_fields.length - 4}</span>
                              )}
                            </span>
                          ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleExpand(log.id)}
                          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                          title={expanded.has(log.id) ? 'Collapse' : 'Show diff'}
                        >
                          <svg className={`w-4 h-4 transition-transform ${expanded.has(log.id) ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {expanded.has(log.id) && (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 bg-white dark:bg-gray-800/80 border-b border-gray-100 dark:border-gray-700">
                          <DiffView log={log} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Z35.1: controls at both top and bottom of the list */}
      {!error && !loading && logs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 mt-4">
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={total} />
        </div>
      )}
    </div>
  )
}
